/**
 * GENERATED — do not edit. Source: vocab.yaml
 * Regenerate with: pnpm --filter @managing-up/schema generate
 */
/** Bumped whenever an axis or a value is added, removed, or reordered. */
export const VOCAB_VERSION = '1.0.0';
/**
 * The closed tag vocabulary. Ordinal axes list `values` in their declared
 * low-to-high order; that ordering is what `max` and `min` compare against.
 */
export const VOCAB = {
    tone: {
        kind: 'nominal',
        label: 'Tone',
        hintLabel: 'the way it was phrased',
        description: 'The social register of the assembly. Nodes filter hard on tone; it is the axis most often responsible for a near miss that the player reads as bad luck.',
        extensible: false,
        values: ['deferential', 'collaborative', 'analytical', 'conservative', 'ambitious', 'trendy', 'informal', 'aggressive'],
        glosses: {
            deferential: 'Defers to the reader\'s judgement. The safe opener register.',
            collaborative: 'Frames the idea as shared work. Warm without being servile.',
            analytical: 'Sober, measured, numbers-forward. The Pedantic QA\'s home register.',
            conservative: 'Cautious, precedent-citing, change-averse.',
            ambitious: 'Career-forward and upward-looking. Sycophant bait.',
            trendy: 'Chases whatever the market said last week. Volatile CMO bait.',
            informal: 'Casual to the point of unseriousness. Reads as junior.',
            aggressive: 'Confrontational, demanding, or blame-assigning. Rarely survives contact.',
        },
    },
    risk: {
        kind: 'ordinal',
        label: 'Risk',
        hintLabel: 'how risky it sounded',
        description: 'Perceived exposure created by the proposal. Risk-Averse nodes test `max risk`; Volatile nodes cheerfully raise it.',
        extensible: false,
        values: ['none', 'low', 'medium', 'high', 'existential'],
        glosses: {
            none: 'Carries no exposure at all. Usually because it commits to nothing.',
            low: 'Reversible, small blast radius, nobody\'s bonus is involved.',
            medium: 'Someone would have to explain it in a meeting.',
            high: 'Someone would have to explain it to the board.',
            existential: 'Company-ending, career-ending, or both. Comedy register.',
        },
    },
    cost: {
        kind: 'ordinal',
        label: 'Cost',
        hintLabel: 'what it looked like it would cost',
        description: 'Perceived budget impact. Distinct from `evidence`: this is the size of the number, not how well the number is supported.',
        extensible: false,
        values: ['zero', 'low', 'moderate', 'high', 'blank_cheque'],
        glosses: {
            zero: 'Explicitly free. The single most persuasive tag in the game.',
            low: 'Absorbable within an existing line item.',
            moderate: 'Needs a budget conversation.',
            high: 'Needs a budget conversation with someone senior.',
            blank_cheque: 'Unbounded. Finance nodes treat this as a disaster trigger.',
        },
    },
    timeline: {
        kind: 'ordinal',
        label: 'Timeline',
        hintLabel: 'when it was supposed to happen',
        description: 'How soon the proposal lands, ordered soonest to furthest out. `max timeline` is the furthest horizon mentioned; `min timeline` is the soonest commitment made.',
        extensible: false,
        values: ['immediate', 'this_week', 'this_quarter', 'next_quarter', 'someday'],
        glosses: {
            immediate: 'Today. Reads as either decisive or panicked.',
            this_week: 'Inside the current sprint. Credible urgency.',
            this_quarter: 'The default corporate horizon.',
            next_quarter: 'Far enough away to be nobody\'s problem yet.',
            someday: 'Roadmap-adjacent. A polite way of saying never.',
        },
    },
    department: {
        kind: 'nominal',
        label: 'Department',
        hintLabel: 'whose remit it fell into',
        description: 'Which function the proposal implicates. Nodes defend their own remit and resent proposals that quietly reassign work to them.',
        extensible: false,
        values: ['engineering', 'ops', 'marketing', 'finance', 'legal', 'hr', 'sales', 'product', 'exec'],
        glosses: {
            engineering: 'Builds it. Resents timelines authored elsewhere.',
            ops: 'Runs it. Resents anything with the word "pilot" in it.',
            marketing: 'Announces it. Occasionally before it exists.',
            finance: 'Funds it. Reads `cost` before it reads anything else.',
            legal: 'Approves it, eventually, with conditions.',
            hr: 'Documents it. Owns the strike ladder.',
            sales: 'Promises it. Frequently already has.',
            product: 'Prioritises it. Owns the roadmap the CEO ignores.',
            exec: 'Decides it. Or forwards it and calls that deciding.',
        },
    },
    concept: {
        kind: 'nominal',
        label: 'Concept',
        hintLabel: 'what it was actually about',
        description: 'The subject matter of the proposal. This is the only extensible axis: a package declares mission-local values in `conceptExtensions` (for example `drones` or `vr_ordering`) and may use them exactly as it uses the shared values below.',
        extensible: true,
        values: ['roi', 'cost_saving', 'revenue', 'efficiency', 'automation', 'innovation', 'compliance', 'customer_trust', 'headcount', 'reliability', 'ceo_favorite', 'profitable', 'viral'],
        glosses: {
            roi: 'Return on investment, asserted rather than demonstrated. The universal solvent of corporate persuasion.',
            cost_saving: 'Spending less on something that already exists.',
            revenue: 'New money, as opposed to money not spent.',
            efficiency: 'The same output with fewer people doing it.',
            automation: 'Efficiency, but with a vendor contract attached.',
            innovation: 'Novelty framed as strategy.',
            compliance: 'Something a regulator would like. Unarguable and unloved.',
            customer_trust: 'Reputation, churn, and the support queue.',
            headcount: 'People, and who reports to whom.',
            reliability: 'It staying up. Ops cares; nobody senior does until it doesn\'t.',
            ceo_favorite: 'The mandate currently coming down from the top, on its merits or otherwise.',
            profitable: 'Framed as margin-positive. Credit Stealer bait.',
            viral: 'Framed as attention-generating. Volatile CMO bait.',
        },
    },
    attribution: {
        kind: 'nominal',
        label: 'Attribution',
        hintLabel: 'whose idea it looked like',
        description: 'Who the proposal appears to be from. Credit Stealer nodes mutate this from `player` to `node`; the `Bcc Shield` perk suppresses that mutation.',
        extensible: false,
        values: ['player', 'node', 'team', 'unattributed'],
        glosses: {
            player: 'The player is visibly the author.',
            node: 'A node has taken the credit. The proposal survives; the player does not benefit.',
            team: 'Credited to a group. Safe, unglamorous, unstealable.',
            unattributed: 'Nobody\'s name on it. Travels fast, rewards no one.',
        },
    },
    evidence: {
        kind: 'ordinal',
        label: 'Evidence',
        hintLabel: 'how well it was backed up',
        description: 'How well the claim is supported, ordered weakest to strongest. This is the axis the Pedantic QA archetype guards; the concept document\'s `cost: detailed` is expressed here as `evidence: data` or better.',
        extensible: false,
        values: ['none', 'anecdote', 'benchmark', 'data', 'audited'],
        glosses: {
            none: 'Asserted. Confidence is doing all the work.',
            anecdote: 'One customer said something once.',
            benchmark: 'Someone else\'s numbers, applied optimistically.',
            data: 'Our own numbers, with a denominator.',
            audited: 'Signed off by a function that does not report to us.',
        },
    },
    audience: {
        kind: 'ordinal',
        label: 'Audience',
        hintLabel: 'how far it was going to travel',
        description: 'Blast radius — how widely the assembly is framed to circulate, ordered narrowest to widest. Rules test `max audience` to catch a player who has quietly escalated something to the board.',
        extensible: false,
        values: ['team', 'department', 'leadership', 'c_suite', 'board', 'public'],
        glosses: {
            team: 'The people already in the channel.',
            department: 'One function, all of it.',
            leadership: 'Directors and VPs. The first genuinely dangerous rung.',
            c_suite: 'Named executives. Overstepping territory.',
            board: 'Governance. Almost always a disaster path.',
            public: 'Outside the company. Reliably a disaster path.',
        },
    },
};
/** Every axis name, in declaration order. */
export const AXES = ['tone', 'risk', 'cost', 'timeline', 'department', 'concept', 'attribution', 'evidence', 'audience'];
/** Axes declared `kind: ordinal`. Only these accept `max` and `min`. */
export const ORDINAL_AXES = ['risk', 'cost', 'timeline', 'evidence', 'audience'];
/** Axes declared `kind: nominal`. */
export const NOMINAL_AXES = ['tone', 'department', 'concept', 'attribution'];
/** Axes a package may extend with mission-local values. */
export const EXTENSIBLE_AXES = ['concept'];
//# sourceMappingURL=vocab.js.map